import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { EditorState, Transaction } from '@milkdown/kit/prose/state';
import { DOMParser, DOMSerializer, Fragment, Slice } from '@milkdown/kit/prose/model';
import type { Node as ProseMirrorNode, Schema } from '@milkdown/kit/prose/model';
import type { DecorationSet, EditorView } from '@milkdown/kit/prose/view';
import { $prose } from '@milkdown/kit/utils';
import { parserCtx } from '@milkdown/kit/core';
import removeMarkdown from '@brainfish-ai/remove-markdown';
import { ySyncPluginKey } from 'y-prosemirror';

import type {
  ReviewSuggestion,
  ReviewSuggestionBulkActions,
  ReviewSuggestionResolveOutcome,
  ReviewSuggestionsResolveSummary,
} from '../../types';
import {
  actualWidgetCountFromDecorations,
  buildDecorationSetFromRanges,
  expectedWidgetCountFromRanges,
  inlineFragWithEdgeWs,
  isFragmentInlineOnly,
  type ParseSuggestionMarkdown,
} from './decorations';
import { mapRanges } from './mapping';
import { resolveReviewSuggestionRanges } from './resolve';
import type { ResolvedReviewSuggestionRange, ReviewSuggestionsMeta, ReviewSuggestionsPluginState } from './types';

export const reviewSuggestionsDecoKey = new PluginKey<ReviewSuggestionsPluginState>('reviewSuggestionsDeco');

/**
 * Meta factory for replacing the suggestion set. Resolves flat-text offsets
 * against the current doc on dispatch.
 *
 * @internal — exported for tests
 */
export const reviewSuggestionsUpdateMeta = (
  suggestions: ReviewSuggestion[] | undefined,
  selectedId: string | null | undefined,
): ReviewSuggestionsMeta => ({ type: 'suggestions', suggestions, selectedId });

/**
 * Meta factory for selection-only changes. Preserves mapped PM ranges.
 *
 * @internal — exported for tests
 */
export const reviewSuggestionsSelectionMeta = (selectedId: string | null | undefined): ReviewSuggestionsMeta => ({
  type: 'selection',
  selectedId,
});

export const reviewSuggestionApplyMeta = (suggestionId: string): ReviewSuggestionsMeta => ({
  type: 'applySuggestion',
  suggestionId,
});

export const reviewSuggestionDismissMeta = (suggestionId: string): ReviewSuggestionsMeta => ({
  type: 'dismissSuggestion',
  suggestionId,
});

export function createReviewSuggestionsInitialState({
  doc,
  suggestions,
  selectedId,
  onOutcome,
  onSummary,
  parseMarkdown,
}: {
  doc: ProseMirrorNode;
  suggestions: ReviewSuggestion[] | undefined;
  selectedId: string | null | undefined;
  onOutcome?: ResolveOutcomeCallback;
  onSummary?: ResolveSummaryCallback;
  parseMarkdown?: ParseSuggestionMarkdown;
}): ReviewSuggestionsPluginState {
  const sourceSuggestions = suggestions ?? [];
  // Collab starts with an empty (pre-sync) doc. Resolving against it reports every
  // suggestion unresolved — provisional noise that the buffered re-resolve later
  // contradicts — so defer resolution and its telemetry until content syncs. A
  // non-empty doc with no matches is a genuine miss: resolve and emit now, and
  // don't keep re-probing on every keystroke.
  const deferResolve = sourceSuggestions.length > 0 && doc.textContent.length === 0;
  const ranges = deferResolve ? [] : resolveReviewSuggestionRanges(doc, sourceSuggestions, onOutcome, onSummary);

  return {
    decorations: buildDecorationSetFromRanges(doc, ranges, selectedId, parseMarkdown),
    ranges,
    suggestions: sourceSuggestions,
    selectedId,
    needsInitialResolve: deferResolve,
  };
}

function createParsedMarkdownSlice(schema: Schema, parsed: ProseMirrorNode, preserveTopLevelBlocks: boolean): Slice | null {
  if (!parsed.content.size) return null;
  if (preserveTopLevelBlocks) return new Slice(parsed.content, 0, 0);

  const dom = DOMSerializer.fromSchema(schema).serializeFragment(parsed.content);

  return DOMParser.fromSchema(schema).parseSlice(dom);
}

function expandWholeTextblockRange(doc: ProseMirrorNode, from: number, to: number): { from: number; to: number } {
  if (from >= to) return { from, to };

  try {
    const $from = doc.resolve(from);
    const $to = doc.resolve(to - 1);
    if ($from.depth === 0 || $from.depth !== $to.depth || $from.parent !== $to.parent) return { from, to };
    if (!$from.parent.isTextblock) return { from, to };
    if ($from.parentOffset !== 0 || $to.parentOffset + 1 !== $to.parent.content.size) return { from, to };

    return { from: $from.before($from.depth), to: $to.after($to.depth) };
  } catch {
    return { from, to };
  }
}

function insertParsedListItemsIntoParentList(tr: Transaction, pos: number, parsed: ProseMirrorNode): boolean {
  if (parsed.childCount !== 1) return false;
  const list = parsed.firstChild;
  if (!list || (list.type.name !== 'ordered_list' && list.type.name !== 'bullet_list')) return false;

  try {
    const $pos = tr.doc.resolve(pos);
    for (let depth = $pos.depth; depth > 1; depth -= 1) {
      const listItem = $pos.node(depth);
      const parentList = $pos.node(depth - 1);
      if (listItem.type.name !== 'list_item' || parentList.type !== list.type) continue;

      const items: ProseMirrorNode[] = [];
      list.forEach((item) => items.push(item));
      tr.replaceRange($pos.after(depth), $pos.after(depth), new Slice(Fragment.fromArray(items), 0, 0));

      return true;
    }
  } catch {
    return false;
  }

  return false;
}

function applyResolvedRangeToTransaction(
  tr: Transaction,
  range: ResolvedReviewSuggestionRange,
  parseMarkdown?: ParseSuggestionMarkdown,
): void {
  if (range.opType === 'delete') {
    if (range.from < range.to) tr.delete(range.from, range.to);

    return;
  }

  const raw = range.replacementText;
  if (!raw) {
    if (range.opType === 'replace' && range.from < range.to) tr.delete(range.from, range.to);

    return;
  }

  if (parseMarkdown) {
    try {
      const parsed = parseMarkdown(raw);
      if (parsed) {
        const fragment = inlineFragWithEdgeWs(tr.doc.type.schema, parsed, raw);
        if (isFragmentInlineOnly(fragment)) {
          // when the fragment is compatible with inline replace,
          // we can replace the text with the fragment
          tr.replace(range.from, range.to, new Slice(fragment, 0, 0));

          return;
        }

        if (range.from === range.to && insertParsedListItemsIntoParentList(tr, range.from, parsed)) return;

        const target = expandWholeTextblockRange(tr.doc, range.from, range.to);
        const slice = createParsedMarkdownSlice(tr.doc.type.schema, parsed, target.from !== range.from || target.to !== range.to);
        if (slice) {
          tr.replaceRange(target.from, target.to, slice);

          return;
        }
      }
    } catch {
      /* fall through */
    }
  }

  const flat = removeMarkdown(raw);
  if (range.opType === 'insert') {
    tr.insertText(flat, range.from);
  } else {
    tr.insertText(flat, range.from, range.to);
  }
}

/**
 * Builds a transaction that applies one suggestion’s `replacementText`.
 *
 * **Markdown:** When `replacementText` can contain markdown (`**bold**`, links, …), pass the same
 * `parseMarkdown` callback the editor plugin uses (`(md) => parseProposedMd(ctx.get(parserCtx), md)`).
 * Omitting `parseMarkdown` applies via stripped plain text (`removeMarkdown`) — inconsistent with chips
 * and with apply paths that supply a Milkdown parser.
 */
export function createApplyReviewSuggestionTransaction(
  state: EditorState,
  suggestionId: string,
  parseMarkdown?: ParseSuggestionMarkdown,
): Transaction | null {
  const pluginState = reviewSuggestionsDecoKey.getState(state);
  const range = pluginState?.ranges.find((item) => item.id === suggestionId);
  if (!range) return null;

  const tr = state.tr;
  applyResolvedRangeToTransaction(tr, range, parseMarkdown);

  return tr.setMeta(reviewSuggestionsDecoKey, reviewSuggestionApplyMeta(suggestionId));
}

export function createDismissReviewSuggestionTransaction(state: EditorState, suggestionId: string): Transaction {
  return state.tr.setMeta(reviewSuggestionsDecoKey, reviewSuggestionDismissMeta(suggestionId));
}

// Safety cap on the bulk iteration loops. Hitting this implies a plugin-state
// bug where a dispatched suggestion failed to drop from `ranges[0]`; we surface
// it via `console.warn` rather than silently truncating.
const BULK_REVIEW_SUGGESTION_ITERATION_CAP = 10_000;

// Cap on collab re-resolve retries. In collab the editor starts with an empty doc
// and content arrives across a handful of Yjs sync transactions, so a small bound
// is ample for sync to settle. Beyond that, anchors that still don't match are
// treated as genuinely-unresolvable (stale review, server-side edits) rather than
// re-probing the full document on every subsequent keystroke.
const MAX_INITIAL_RESOLVE_ATTEMPTS = 30;

/**
 * Apply every active review suggestion against the live view, in order.
 * Each apply re-reads plugin state, so ranges that get invalidated by a
 * prior apply are skipped naturally. Returns the ids that were applied.
 *
 * Pass **`parseMarkdown` in sync with** {@link createApplyReviewSuggestionTransaction} when proposals may be markdown.
 */
export function applyAllReviewSuggestions(view: EditorView, parseMarkdown?: ParseSuggestionMarkdown): string[] {
  const applied: string[] = [];
  let i = 0;
  for (; i < BULK_REVIEW_SUGGESTION_ITERATION_CAP; i++) {
    const pluginState = reviewSuggestionsDecoKey.getState(view.state);
    const next = pluginState?.ranges[0];
    if (!next) break;
    const tr = createApplyReviewSuggestionTransaction(view.state, next.id, parseMarkdown);
    if (!tr) break;
    view.dispatch(tr);
    applied.push(next.id);
  }
  if (i === BULK_REVIEW_SUGGESTION_ITERATION_CAP) {
    console.warn(
      `applyAllReviewSuggestions: hit iteration cap (${BULK_REVIEW_SUGGESTION_ITERATION_CAP}); plugin state likely failed to drop a suggestion after dispatch.`,
    );
  }

  return applied;
}

/**
 * Reject every active review suggestion. Doc is unchanged; only decorations
 * clear. The internal meta type is `dismissSuggestion` — the external "reject"
 * naming matches the user-facing button label. Returns the ids that were
 * rejected.
 */
export function rejectAllReviewSuggestions(view: EditorView): string[] {
  const rejected: string[] = [];
  let i = 0;
  for (; i < BULK_REVIEW_SUGGESTION_ITERATION_CAP; i++) {
    const pluginState = reviewSuggestionsDecoKey.getState(view.state);
    const next = pluginState?.ranges[0];
    if (!next) break;
    const tr = createDismissReviewSuggestionTransaction(view.state, next.id);
    if (!tr) break;
    view.dispatch(tr);
    rejected.push(next.id);
  }
  if (i === BULK_REVIEW_SUGGESTION_ITERATION_CAP) {
    console.warn(
      `rejectAllReviewSuggestions: hit iteration cap (${BULK_REVIEW_SUGGESTION_ITERATION_CAP}); plugin state likely failed to drop a suggestion after dispatch.`,
    );
  }

  return rejected;
}

/**
 * Builds bulk accept/reject callbacks and optional `getMarkdown` for the editor `view`.
 *
 * @param parseMarkdown Same parser callback as Milkdown-hosted suggestions; required for markdown-accurate apply-all
 * when `replacementText`/proposals may be markdown. See {@link createApplyReviewSuggestionTransaction}.
 */
export function createReviewSuggestionBulkActions(
  view: EditorView,
  getMarkdown?: () => string,
  parseMarkdown?: ParseSuggestionMarkdown,
): ReviewSuggestionBulkActions {
  return {
    applyAllReviewSuggestions: () => applyAllReviewSuggestions(view, parseMarkdown),
    rejectAllReviewSuggestions: () => rejectAllReviewSuggestions(view),
    getResolvedReviewSuggestionCount: () => reviewSuggestionsDecoKey.getState(view.state)?.ranges.length ?? 0,
    getMarkdown,
  };
}

type ResolveOutcomeCallback = (suggestionId: string, outcome: ReviewSuggestionResolveOutcome) => void;
type ResolveOutcomeRef = { current?: ResolveOutcomeCallback | null };
type ResolveSummaryCallback = (summary: ReviewSuggestionsResolveSummary) => void;
type ResolveSummaryRef = { current?: ResolveSummaryCallback | null };

function emitOutcomes(ref: ResolveOutcomeRef | undefined): ResolveOutcomeCallback | undefined {
  if (!ref) return undefined;

  return (id, outcome) => {
    try {
      ref.current?.(id, outcome);
    } catch {
      // Telemetry must never break the editor: swallow producer errors.
    }
  };
}

function emitSummary(ref: ResolveSummaryRef | undefined): ResolveSummaryCallback | undefined {
  if (!ref) return undefined;

  return (summary) => {
    try {
      ref.current?.(summary);
    } catch {
      // Host persistence must never break the editor.
    }
  };
}

function getSuggestionIdFromTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return null;

  return target.closest<HTMLElement>('[data-review-suggestion-id]')?.dataset.reviewSuggestionId ?? null;
}

function hasInlineCoverageGapInRange(decorations: DecorationSet, from: number, to: number): boolean {
  const spans = decorations
    .find(from, to)
    .filter((deco) => deco.to > deco.from)
    .sort((a, b) => (a.from === b.from ? a.to - b.to : a.from - b.from));
  let cursor = from;
  for (const span of spans) {
    const spanFrom = Math.max(span.from, from);
    const spanTo = Math.min(span.to, to);
    if (spanTo <= spanFrom || spanTo <= cursor) continue;
    if (spanFrom > cursor) return true;
    cursor = Math.max(cursor, spanTo);
    if (cursor >= to) return false;
  }

  return cursor < to;
}

function hasInlineCoverageGapFromRanges(ranges: ResolvedReviewSuggestionRange[], decorations: DecorationSet): boolean {
  for (const range of ranges) {
    if (range.opType === 'insert' || range.from >= range.to) continue;
    if (hasInlineCoverageGapInRange(decorations, range.from, range.to)) return true;
  }

  return false;
}

type ProtectedRange = { from: number; to: number };

function toProtectedRanges(ranges: ResolvedReviewSuggestionRange[]): ProtectedRange[] {
  return ranges.filter((range) => range.opType !== 'insert' && range.from < range.to).map((range) => ({ from: range.from, to: range.to }));
}

function stepChangeTouchesProtectedRanges(oldFrom: number, oldTo: number, protectedRanges: ProtectedRange[]): boolean {
  for (const protectedRange of protectedRanges) {
    if (oldFrom === oldTo) {
      // Insertion is blocked only when it lands strictly inside doomed text.
      if (oldFrom > protectedRange.from && oldFrom < protectedRange.to) return true;
      continue;
    }
    // Any overlap with a delete/replace span edits doomed text.
    if (oldFrom < protectedRange.to && oldTo > protectedRange.from) return true;
  }

  return false;
}

function transactionTouchesDoomedText(tr: Transaction, ranges: ProtectedRange[]) {
  if (!tr.docChanged || ranges.length === 0) return false;

  for (let stepIndex = 0; stepIndex < tr.mapping.maps.length; stepIndex += 1) {
    const stepMap = tr.mapping.maps[stepIndex];
    const mapToStep = tr.mapping.slice(0, stepIndex);
    const rangesAtStep = ranges
      .map((range) => ({
        from: mapToStep.map(range.from, 1),
        to: mapToStep.map(range.to, -1),
      }))
      .filter((range) => range.from < range.to);

    let touchesDoomedText = false;
    stepMap.forEach((oldFrom, oldTo) => {
      if (touchesDoomedText) return;
      touchesDoomedText = stepChangeTouchesProtectedRanges(oldFrom, oldTo, rangesAtStep);
    });

    if (touchesDoomedText) return true;
  }

  return false;
}

/** True if the mapping deleted the content under any range endpoint, so positional mapping can't track it. */
function rangesDetachedByMapping(ranges: ResolvedReviewSuggestionRange[], mapping: Transaction['mapping']): boolean {
  for (const range of ranges) {
    if (mapping.mapResult(range.from, 1).deleted) return true;
    if (range.opType !== 'insert' && mapping.mapResult(range.to, -1).deleted) return true;
  }

  return false;
}

export function buildReviewSuggestionsPlugin(
  initialSuggestions: ReviewSuggestion[] | undefined,
  initialSelectedId: string | null | undefined,
  onReviewSuggestionSelectRef?: { current?: ((suggestionId: string) => void) | null },
  onReviewSuggestionResolveRef?: ResolveOutcomeRef,
  parseMarkdown?: ParseSuggestionMarkdown,
  onReviewSuggestionsResolvedRef?: ResolveSummaryRef,
): Plugin<ReviewSuggestionsPluginState> {
  const onOutcome = emitOutcomes(onReviewSuggestionResolveRef);
  const onSummary = emitSummary(onReviewSuggestionsResolvedRef);

  return new Plugin<ReviewSuggestionsPluginState>({
    key: reviewSuggestionsDecoKey,
    filterTransaction(tr, state) {
      if (!tr.docChanged) return true;
      const meta = tr.getMeta(reviewSuggestionsDecoKey) as ReviewSuggestionsMeta | undefined;
      if (meta?.type === 'applySuggestion') return true;
      // Remote Yjs sync transactions must never be filtered: rejecting one
      // leaves the local PM doc out of sync with the shared Y.Doc and will
      // break subsequent edits. Doomed-text immutability is a local-UX
      // constraint, so only block transactions that originated here.
      if (tr.getMeta(ySyncPluginKey)) return true;
      const pluginState = reviewSuggestionsDecoKey.getState(state);
      if (!pluginState) return true;

      return !transactionTouchesDoomedText(tr, toProtectedRanges(pluginState.ranges));
    },
    state: {
      init(_, state) {
        return createReviewSuggestionsInitialState({
          doc: state.doc,
          suggestions: initialSuggestions,
          selectedId: initialSelectedId,
          onOutcome,
          onSummary,
          parseMarkdown,
        });
      },
      apply(tr, prev) {
        const meta = tr.getMeta(reviewSuggestionsDecoKey) as ReviewSuggestionsMeta | undefined;

        if (meta?.type === 'suggestions') {
          // Full replacement: offsets are for the CURRENT doc, so rebuild from source
          // via the same path as init (incl. the empty-doc resolve deferral).
          return createReviewSuggestionsInitialState({
            doc: tr.doc,
            suggestions: meta.suggestions,
            selectedId: meta.selectedId,
            onOutcome,
            onSummary,
            parseMarkdown,
          });
        }

        if (meta?.type === 'selection') {
          // Selection-only intent: preserve existing mapped ranges to avoid
          // offset drift from unnecessary re-resolve.
          const ranges = tr.docChanged ? mapRanges(prev.ranges, tr.mapping) : prev.ranges;

          return {
            decorations: buildDecorationSetFromRanges(tr.doc, ranges, meta.selectedId, parseMarkdown),
            ranges,
            suggestions: prev.suggestions,
            selectedId: meta.selectedId,
            needsInitialResolve: prev.needsInitialResolve,
          };
        }

        if (meta?.type === 'applySuggestion' || meta?.type === 'dismissSuggestion') {
          const ranges = mapRanges(
            prev.ranges.filter((range) => range.id !== meta.suggestionId),
            tr.mapping,
          );
          const selectedId = prev.selectedId === meta.suggestionId ? null : prev.selectedId;

          return {
            decorations: buildDecorationSetFromRanges(tr.doc, ranges, selectedId, parseMarkdown),
            ranges,
            suggestions: prev.suggestions.filter((suggestion) => suggestion.id !== meta.suggestionId),
            selectedId,
            needsInitialResolve: false,
          };
        }

        if (tr.docChanged) {
          if (prev.needsInitialResolve) {
            // Collab mode starts with an empty doc; the real content arrives across
            // one or more Yjs sync transactions. Re-resolve on each docChange but
            // DON'T lock in the result (or emit telemetry) until a resolution actually
            // matches — clearing the flag on a partial sync would resolve against
            // incomplete text and leave every later sync to only map the stale ranges
            // forward, sliding decorations to the document end.
            //
            // Resolve once and buffer outcomes; emit them only when we settle, so the
            // empty intermediate syncs stay silent and a single resolve does the work.
            const bufferedOutcomes: Array<[string, ReviewSuggestionResolveOutcome]> = [];
            let bufferedSummary: ReviewSuggestionsResolveSummary | undefined;
            const ranges = resolveReviewSuggestionRanges(
              tr.doc,
              prev.suggestions,
              (id, outcome) => bufferedOutcomes.push([id, outcome]),
              (summary) => {
                bufferedSummary = summary;
              },
            );

            // Give up after a bounded number of attempts so anchors that never match
            // the synced doc don't re-probe the whole document on every keystroke.
            const attempts = (prev.initialResolveAttempts ?? 0) + 1;
            const settled = ranges.length > 0 || attempts >= MAX_INITIAL_RESOLVE_ATTEMPTS;
            if (!settled) {
              // Still waiting for content. prev.ranges is empty, so the (empty)
              // decoration set just needs to stay valid against the new doc.
              return { ...prev, decorations: prev.decorations.map(tr.mapping, tr.doc), initialResolveAttempts: attempts };
            }

            // Resolved, or out of retries: emit the buffered outcomes/summary against
            // the authoritative doc exactly once, and stop probing.
            bufferedOutcomes.forEach(([id, outcome]) => onOutcome?.(id, outcome));
            if (bufferedSummary) onSummary?.(bufferedSummary);

            return {
              ...prev,
              decorations: buildDecorationSetFromRanges(tr.doc, ranges, prev.selectedId, parseMarkdown),
              ranges,
              needsInitialResolve: false,
              initialResolveAttempts: attempts,
            };
          }

          // Remote Yjs sync can replace doomed text (filterTransaction only blocks
          // local edits). When it detaches a range — e.g. the server snapshot
          // replacing the whole doc after the first resolve — positional mapping
          // slides the decorations to the doc end, so re-resolve from anchors
          // instead. Local edits keep exact mapping; re-resolving would drift.
          if (tr.getMeta(ySyncPluginKey) && rangesDetachedByMapping(prev.ranges, tr.mapping)) {
            const reResolved = resolveReviewSuggestionRanges(tr.doc, prev.suggestions, onOutcome, onSummary);

            return {
              ...prev,
              decorations: buildDecorationSetFromRanges(tr.doc, reResolved, prev.selectedId, parseMarkdown),
              ranges: reResolved,
            };
          }

          // Hot path for typing/edits.
          const ranges = mapRanges(prev.ranges, tr.mapping);

          const mappedDecorations = prev.decorations.map(tr.mapping, tr.doc);
          const expectedWidgetCount = expectedWidgetCountFromRanges(ranges);
          const mappedWidgetCount = actualWidgetCountFromDecorations(mappedDecorations);
          const widgetLostOnMap = mappedWidgetCount !== expectedWidgetCount;
          const inlineCoverageGapOnMap = hasInlineCoverageGapFromRanges(ranges, mappedDecorations);

          return {
            ...prev,
            // Guard against PM map drift: if mapped decorations lose widget count
            // or leave a gap inside a tracked doomed range, rebuild from ranges.
            decorations:
              widgetLostOnMap || inlineCoverageGapOnMap
                ? buildDecorationSetFromRanges(tr.doc, ranges, prev.selectedId, parseMarkdown)
                : mappedDecorations,
            ranges,
          };
        }

        return prev;
      },
    },
    props: {
      decorations(state) {
        return reviewSuggestionsDecoKey.getState(state)?.decorations;
      },
      handleClick: (_view, _pos, event) => {
        const suggestionId = getSuggestionIdFromTarget(event.target);
        if (!suggestionId) return false;
        onReviewSuggestionSelectRef?.current?.(suggestionId);

        return true;
      },
    },
  });
}

/** Prefix each line so CommonMark parses a blockquote (paragraph) from inline-only fragments. */
function quoteLinesAsBlock(block: string): string {
  const lines = block.split('\n');

  return `${lines.map((line) => `> ${line}`).join('\n')}\n`;
}

/**
 * Milkdown's `parser` expects document-shaped markdown. Bare inline fragments can
 * yield an empty `doc`. Prefer a cheap fixed sequence over many parse attempts:
 * 1. Ensure a terminating blank line after the last block (CommonMark block boundary).
 * 2. If still empty, wrap as a blockquote paragraph (stable for phrasing markdown).
 *
 * Exported so accept/bulk-actions use the same behavior as suggestion chips.
 */
export function parseProposedMd(
  parser: (md: string) => ProseMirrorNode | null | undefined,
  md: string,
): ProseMirrorNode | null | undefined {
  if (!md) return null;
  const trimmed = md.trim();
  if (!trimmed) return null;

  const parseOnce = (source: string) => {
    try {
      const out = parser(source);
      if (out != null && out.content.size > 0) return out;
    } catch {
      /* caller may try fallback */
    }

    return null;
  };

  const withBlockBoundary = md.endsWith('\n\n') ? md : `${md.trimEnd()}\n\n`;
  const normalized = parseOnce(withBlockBoundary);
  if (normalized) return normalized;

  return parseOnce(quoteLinesAsBlock(trimmed));
}

export function reviewSuggestionsDecorationPlugin(
  onReviewSuggestionSelectRef?: { current?: ((suggestionId: string) => void) | null },
  onReviewSuggestionResolveRef?: ResolveOutcomeRef,
  onReviewSuggestionsResolvedRef?: ResolveSummaryRef,
) {
  return $prose((ctx) => {
    const parseMarkdown: ParseSuggestionMarkdown = (md) => parseProposedMd(ctx.get(parserCtx), md);

    return buildReviewSuggestionsPlugin(
      undefined,
      null,
      onReviewSuggestionSelectRef,
      onReviewSuggestionResolveRef,
      parseMarkdown,
      onReviewSuggestionsResolvedRef,
    );
  });
}
